import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import type { Course, DonneesOps } from '../../api/types'
import { depuis, etatPosition } from '../../api/geo'
import { pointsBoitiers } from '../../api/gps'
import { CarteCourse } from '../CarteCourse'
import { DetailCourse } from '../DetailCourse'
import { etatEncaissement, peutEncaisser, soldeCourse } from '../../api/encaissement'
import { useDroits } from '../Contexte'
import { ModaleEncaissement } from '../Encaissement'
import { dateCourte, estAujourdHui, fcfa } from '../Format'
import { Icone } from '../Icone'

/* La carte (Leaflet + MapLibre, ~1 Mo) est chargée À LA DEMANDE, à la
   première ouverture du dispatch : la prise d'appel s'ouvre sans la payer.
   Au Mali, les forfaits de données sont comptés. */
const CarteLivreurs = lazy(() => import('../CarteLivreurs').then((m) => ({ default: m.CarteLivreurs })))
import { TourneesLivreurs } from '../TourneesLivreurs'
import type { TraceCarte } from '../../api/itineraire'

function s(n: number): string {
  return n > 1 ? 's' : ''
}

/**
 * Le dispatch — transposition de `renderDispatch()`, plus la CARTE des
 * livreurs (positions en direct) et le détail des courses livrées, preuve
 * de livraison comprise.
 */
export function EcranDispatch({
  donnees,
  surAssigner,
  surAvancer,
  surAnnuler,
  surNotifier,
  surRafraichir,
  surNouvelleCourse,
  surEcrireClient,
}: {
  donnees: DonneesOps
  /** Ouvre la création manuelle d'une course (l'ancienne « prise d'appel »), pour les exceptions. */
  surNouvelleCourse?: () => void
  /** Ouvre la messagerie sur ce client, la course en contexte (0067). Absent sans droit de messagerie. */
  surEcrireClient?: (clientId: string, courseId: string | null) => void
  surAssigner: (courseId: string, livreurId: string) => void
  surAvancer: (course: Course) => void
  surAnnuler: (courseId: string) => void
  surNotifier?: (message: string) => void
  /** Recharge l'état : nécessaire à l'enregistrement d'un encaissement depuis la liste ou la fiche. */
  surRafraichir?: () => Promise<void>
}) {
  /* L'horloge avance toute seule : sans elle, une position deviendrait
     « ancienne » sans que l'écran le montre tant qu'aucun livreur ne bouge. */
  const [maintenant, setMaintenant] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setMaintenant(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const [detail, setDetail] = useState<Course | null>(null)
  const [aEncaisser, setAEncaisser] = useState<Course | null>(null)
  /* Itinéraire d'une course ou tournée d'un livreur, dessiné sur la carte (0066). */
  const [trace, setTrace] = useState<TraceCarte | null>(null)
  const vehiculesABoitier = useMemo(
    () => pointsBoitiers(donnees.boitiers, donnees.vehicules, donnees.drivers, maintenant),
    [donnees.boitiers, donnees.vehicules, donnees.drivers, maintenant],
  )
  const [recherche, setRecherche] = useState('')

  const actives = donnees.courses.filter((c) => ['creee', 'assignee', 'collecte', 'en_livraison'].includes(c.statut))
  const livrees = donnees.courses.filter((c) => c.statut === 'livree' && estAujourdHui(c.delivered_at))

  const etats = donnees.drivers.map((d) => ({ d, pos: etatPosition(d, maintenant) }))
  const recentes = etats.filter((x) => x.pos.etat === 'recente').length
  const sansPosition = etats.filter((x) => x.pos.etat !== 'recente')

  const trouvees = useMemo(() => {
    const q = recherche.trim().toLowerCase()
    if (q.length < 3) return []
    return donnees.livrees
      .filter((c) => c.ref.toLowerCase().includes(q) || (donnees.clients.find((x) => x.id === c.client_account_id)?.name ?? '').toLowerCase().includes(q))
      .slice(-20)
      .reverse()
  }, [recherche, donnees.livrees, donnees.clients])

  return (
    <>
      {surNouvelleCourse && (
        <div className="barre-actions">
          <span className="t-muted">Les courses arrivent de Danaya, de l’API et de l’app. Pour une exception, créez-la à la main.</span>
          <button className="btn btn-sm" onClick={surNouvelleCourse}>
            <Icone nom="plus" /> Course manuelle
          </button>
        </div>
      )}
      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <span className="h-ic">
                <Icone nom="dispatch" />
              </span>{' '}
              Carte des livreurs
            </h3>
            <div className="ch-sub">
              {recentes} position{s(recentes)} en direct sur {donnees.drivers.length} livreur{s(donnees.drivers.length)}
              {vehiculesABoitier.length > 0 && ` · ${vehiculesABoitier.length} véhicule${s(vehiculesABoitier.length)} à boîtier`} · mise à jour en temps réel
            </div>
          </div>
        </div>
        <div className="card-body">
          <Suspense fallback={<div className="carte-livreurs" aria-busy="true" />}>
            <CarteLivreurs livreurs={donnees.drivers} courses={donnees.courses} maintenant={maintenant} boitiers={vehiculesABoitier} trace={trace} />
          </Suspense>
          {trace && (
            <div className="bandeau-trace" aria-live="polite">
              <span>
                <span className="puce" style={{ background: '#0F8A6A' }} /> {trace.titre}
                {trace.arrets.length > 0 && ` · ${trace.arrets.length} arrêt${s(trace.arrets.length)}`}
              </span>
              <button className="btn btn-outline btn-sm" onClick={() => setTrace(null)}>
                Effacer le tracé
              </button>
            </div>
          )}
          <div className="legende-carte" style={{ marginTop: 10 }}>
            <span><span className="puce" style={{ background: '#1A7A4A' }} /> Disponible</span>
            <span><span className="puce" style={{ background: '#C2620A' }} /> En course</span>
            <span><span className="puce" style={{ background: '#5A6B65' }} /> Hors ligne</span>
            <span><span className="puce" style={{ background: '#fff', border: '2px dashed #87978F' }} /> Position de plus de 10 min</span>
            {vehiculesABoitier.length > 0 && (
              <span><span className="puce" style={{ background: '#2B5FA8', borderRadius: 3 }} /> Véhicule à boîtier GPS</span>
            )}
          </div>
          {sansPosition.length > 0 && (
            <div className="liste-sans-position" style={{ marginTop: 10 }}>
              <b>Sans position récente :</b>{' '}
              {sansPosition
                .map(({ d, pos }) => `${d.full_name} (${pos.etat === 'aucune' ? 'jamais localisé' : `vu ${depuis(pos.ageMs)}`})`)
                .join(' · ')}
            </div>
          )}
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="dispatch" />
                  </span>{' '}
                  Courses en cours
                </h3>
                <div className="ch-sub">
                  {actives.length} course{s(actives.length)} en cours · tous clients
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="course-list">
                {actives.length === 0 ? (
                  <div className="no-hit">Aucune course en cours</div>
                ) : (
                  actives.map((c) => (
                    <CarteCourse
                      key={c.id}
                      course={c}
                      donnees={donnees}
                      surAssigner={surAssigner}
                      surAvancer={surAvancer}
                      surAnnuler={surAnnuler}
                      surDetail={setDetail}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="spark" />
                  </span>{' '}
                  Tournées
                </h3>
                <div className="ch-sub">Ordre de passage le plus court, à moto, pour chaque livreur en course</div>
              </div>
            </div>
            <div className="card-body">
              <TourneesLivreurs livreurs={donnees.drivers} courses={donnees.courses} traceActive={trace?.titre ?? null} surTracer={setTrace} />
            </div>
          </div>
        </div>

        <div>
          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="check" />
                  </span>{' '}
                  Livrées aujourd'hui
                </h3>
                <div className="ch-sub">
                  {livrees.length} livraison{s(livrees.length)} terminée{s(livrees.length)} · cliquez pour la preuve
                </div>
              </div>
            </div>
            <div className="card-body flush">
              <TableLivrees courses={livrees} donnees={donnees} surDetail={setDetail} surEncaisser={surRafraichir ? setAEncaisser : undefined} vide="Rien encore aujourd'hui" />
            </div>
          </div>

          <div className="card" data-anim>
            <div className="card-head">
              <div>
                <h3>
                  <span className="h-ic">
                    <Icone nom="book" />
                  </span>{' '}
                  Retrouver une livraison
                </h3>
                <div className="ch-sub">Par référence ou nom du client — toutes les courses livrées</div>
              </div>
            </div>
            <div className="card-body">
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="rech-livree">Référence ou client</label>
                <input
                  id="rech-livree"
                  placeholder="ex. CMD-ABB328"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            {recherche.trim().length >= 3 && (
              <div className="card-body flush">
                <TableLivrees courses={trouvees} donnees={donnees} surDetail={setDetail} surEncaisser={surRafraichir ? setAEncaisser : undefined} vide="Aucune course livrée ne correspond" avecDate />
              </div>
            )}
          </div>
        </div>
      </div>

      {detail && (
        /* La fiche relit la course dans l'état en direct : la position envoyée par le destinataire, ou le
           prix recalculé, s'affichent sans fermer ni rouvrir. */
        <DetailCourse
          course={[...donnees.courses, ...donnees.livrees].find((c) => c.id === detail.id) ?? detail}
          donnees={donnees}
          surFermer={() => setDetail(null)}
          {...(surEcrireClient ? { surEcrireClient: (clientId: string, courseId: string) => { setDetail(null); surEcrireClient(clientId, courseId) } } : {})}
          surTracer={(t) => {
            setTrace(t)
            setDetail(null)
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
          {...(surNotifier ? { surNotifier } : {})}
          {...(surRafraichir ? { surRafraichir } : {})}
        />
      )}

      {aEncaisser && surRafraichir && (
        <ModaleEncaissement
          course={aEncaisser}
          client={donnees.tousClients.find((c) => c.id === aEncaisser.client_account_id)}
          donnees={donnees}
          du={soldeCourse(aEncaisser, donnees.encaissements).du}
          surFermer={() => setAEncaisser(null)}
          surFait={async (message) => {
            setAEncaisser(null)
            surNotifier?.(message)
            await surRafraichir()
          }}
        />
      )}
    </>
  )
}

function TableLivrees({
  courses,
  donnees,
  surDetail,
  surEncaisser,
  vide,
  avecDate = false,
}: {
  courses: Course[]
  donnees: DonneesOps
  surDetail: (c: Course) => void
  /** Absent = pas de bouton d'encaissement (l'écran appelant ne sait pas recharger). */
  surEncaisser: ((c: Course) => void) | undefined
  vide: string
  avecDate?: boolean
}) {
  const droits = useDroits()
  const peutEnregistrer = droits.peut('encaissements') && !droits.apercu
  return (
    <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th>Réf</th>
            {avecDate && <th>Date</th>}
            <th>Client</th>
            <th>Trajet</th>
            <th>Prix</th>
            <th>Encaissement</th>
            <th>Preuve</th>
          </tr>
        </thead>
        <tbody>
          {courses.length === 0 ? (
            <tr>
              <td colSpan={avecDate ? 7 : 6} className="t-muted">
                {vide}
              </td>
            </tr>
          ) : (
            courses.map((c) => {
              const client = donnees.tousClients.find((x) => x.id === c.client_account_id)
              return (
                <tr
                  key={c.id}
                  style={{ cursor: 'pointer' }}
                  tabIndex={0}
                  onClick={() => surDetail(c)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && surDetail(c)}
                  aria-label={`Détail de la course ${c.ref}`}
                >
                  <td className="mono">{c.ref}</td>
                  {avecDate && <td className="t-muted">{dateCourte(c.delivered_at)}</td>}
                  <td className="t-strong">{client?.name ?? '—'}</td>
                  <td className="t-muted">
                    {c.pickup} → {c.dropoff}
                  </td>
                  <td className="mono">{fcfa(c.price_fcfa)}</td>
                  <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {client && client.type !== 'particulier' ? (
                      <span className="t-muted" title="Les courses d'une PME se règlent par facture">Facture</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        <span className={`badge ${etatEncaissement(c, donnees.encaissements).pastille} plain`}>{etatEncaissement(c, donnees.encaissements).libelle}</span>
                        {surEncaisser && peutEnregistrer && peutEncaisser(c, client, donnees.billed, donnees.encaissements).ok && (
                          <button className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => surEncaisser(c)}>
                            Encaissement reçu
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {c.proof_photo_path ? (
                      <span className="badge b-green plain">Photo</span>
                    ) : c.proof_recipient_name ? (
                      <span className="badge b-blue plain">Nom</span>
                    ) : (
                      <span className="badge b-gray plain">Aucune</span>
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
